import { useState, useCallback, useEffect, useRef } from "react";
import AssetTile from "../AssetTile/AssetTile";
import { GroupedInvestmentCategory } from "../../types";
import { useUpdateInvestmentPreferences } from "hooks/investments";
import type { InvestmentTypePreference } from "hooks/investments/UpdateInvestmentPreferencesHook";
import "./AssetTilesList.scss";

interface AssetTilesListProps {
  categories: Record<string, GroupedInvestmentCategory>;
  title?: string;
  banner?: React.ReactNode;
  isOwn?: boolean;
  isArchived?: boolean;
  otherSectionCategories?: Record<string, GroupedInvestmentCategory>;
  pendingOpenUuid?: string | null;
  onPendingOpenComplete?: () => void;
}

const AssetTilesList = ({
  categories,
  title,
  banner,
  isOwn = false,
  isArchived = false,
  otherSectionCategories,
  pendingOpenUuid,
  onPendingOpenComplete,
}: AssetTilesListProps) => {
  const getSortedCategories = useCallback(() => {
    return Object.values(categories).sort((a, b) => a.order - b.order);
  }, [categories]);

  const [sortedCategories, setSortedCategories] =
    useState<GroupedInvestmentCategory[]>(getSortedCategories);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const { mutate: updatePreferences } = useUpdateInvestmentPreferences();

  const touchDragIndex = useRef<number | null>(null);

  const handleTouchStart = (index: number) => {
    touchDragIndex.current = index;
    setDragIndex(index);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const tileEl = el?.closest("[data-tile-index]");
    if (!tileEl) return;
    const overIndex = Number(tileEl.getAttribute("data-tile-index"));
    if (!isNaN(overIndex) && overIndex !== touchDragIndex.current) {
      setDragOverIndex(overIndex);
    }
  };

  const handleTouchEnd = () => {
    handleDragEnd();
    touchDragIndex.current = null;
  };

  useEffect(() => {
    setSortedCategories(getSortedCategories());
  }, [getSortedCategories]);

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.dataTransfer.setDragImage(
      e.currentTarget,
      e.clientX - rect.left,
      e.clientY - rect.top,
    );
    setDragIndex(index);
  };

  const handleDragEnter = (index: number) => {
    if (dragIndex === null || dragIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex === null || dragOverIndex === null) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newCategories = [...sortedCategories];
    const [draggedItem] = newCategories.splice(dragIndex, 1);
    newCategories.splice(dragOverIndex, 0, draggedItem);

    const reorderedCategories = newCategories.map((cat, index) => ({
      ...cat,
      order: index,
    }));

    setSortedCategories(reorderedCategories);

    const section: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "own_archived"
        : "reliance_archived"
      : isOwn
      ? "own"
      : "reliance";
    const preferences = reorderedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      section,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    const otherSection: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "reliance_archived"
        : "own_archived"
      : isOwn
      ? "reliance"
      : "own";
    const otherPreferences = Object.values(otherSectionCategories ?? {}).map(
      (cat) => ({
        slug: cat.investmentType.slug,
        section: otherSection,
        order: cat.order,
        isExpanded: cat.isExpanded,
      }),
    );

    updatePreferences({ preferences: [...preferences, ...otherPreferences] });

    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleToggleExpand = (slug: string, isExpanded: boolean) => {
    const updatedCategories = sortedCategories.map((cat) =>
      cat.investmentType.slug === slug ? { ...cat, isExpanded } : cat,
    );

    setSortedCategories(updatedCategories);

    const section: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "own_archived"
        : "reliance_archived"
      : isOwn
      ? "own"
      : "reliance";
    const preferences = updatedCategories.map((cat) => ({
      slug: cat.investmentType.slug,
      section,
      order: cat.order,
      isExpanded: cat.isExpanded,
    }));

    const otherSection: InvestmentTypePreference["section"] = isArchived
      ? isOwn
        ? "reliance_archived"
        : "own_archived"
      : isOwn
      ? "reliance"
      : "own";
    const otherPreferences = Object.values(otherSectionCategories ?? {}).map(
      (cat) => ({
        slug: cat.investmentType.slug,
        section: otherSection,
        order: cat.order,
        isExpanded: cat.isExpanded,
      }),
    );

    updatePreferences({ preferences: [...preferences, ...otherPreferences] });
  };

  if (sortedCategories.length === 0) {
    return null;
  }

  return (
    <div className="asset-tiles-list">
      {title && <h3 className="asset-tiles-list__title">{title}</h3>}
      {banner && <div className="asset-tiles-list__banner">{banner}</div>}
      <div className="asset-tiles-list__items">
        {sortedCategories.map((category, index) => (
          <AssetTile
            key={category.investmentType.slug}
            category={category}
            index={index}
            isOwn={isOwn}
            isArchived={isArchived}
            onToggleExpand={handleToggleExpand}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragEnd={handleDragEnd}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            isDragging={dragIndex === index}
            isDragOver={dragOverIndex === index}
            isDragEnabled={sortedCategories.length >= 2}
            pendingOpenUuid={pendingOpenUuid}
            onPendingOpenComplete={onPendingOpenComplete}
          />
        ))}
      </div>
    </div>
  );
};

export default AssetTilesList;
